import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Plus, Search } from 'lucide-react';
import { useI18n, useTranslation } from '@solara/i18n';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Select } from '@solara/ui-shared';
import { useTenants } from '@solara/api-client/react';
import { adaptTenants, type AdminTenantRow } from '../data/adapters';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

type StatusFilter = 'all' | AdminTenantRow['onboardingStatus'];

export default function TenantsPage() {
  const { t } = useTranslation('admin');
  const { locale } = useI18n();
  const numberFmt = new Intl.NumberFormat(locale);

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');

  const tenantsQuery = useTenants({
    search: query || undefined,
    status: status === 'all' ? undefined : status,
  });
  const tenants = useMemo(
    () => adaptTenants(tenantsQuery.data),
    [tenantsQuery.data],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return tenants.filter((tn) => {
      if (status !== 'all' && tn.onboardingStatus !== status) return false;
      if (!q) return true;
      return (
        tn.companyNameEn.toLowerCase().includes(q) ||
        tn.companyNameAr.includes(q) ||
        tn.trn.includes(q) ||
        tn.peppolId.toLowerCase().includes(q)
      );
    });
  }, [tenants, query, status]);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('tenants.title')}</h1>
          <p className="text-fg-muted">{t('tenants.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" />
            <input
              type="search"
              placeholder={t('tenants.search')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-10 ps-9 pe-3 rounded-md bg-surface border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/60 w-full sm:w-72"
            />
          </div>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
            options={[
              { value: 'all', label: t('common.all') },
              { value: 'PENDING', label: t('tenants.status.PENDING') },
              { value: 'ACTIVE', label: t('tenants.status.ACTIVE') },
              { value: 'SUSPENDED', label: t('tenants.status.SUSPENDED') },
              { value: 'TERMINATED', label: t('tenants.status.TERMINATED') },
            ]}
            containerClassName="w-44"
          />
          <Button variant="primary" className="gap-2">
            <Plus className="h-4 w-4" />
            {t('tenants.newTenant')}
          </Button>
        </div>
      </header>

      {tenantsQuery.isLoading ? <LoadingBlock /> : tenantsQuery.error ? (
        <ErrorBanner error={tenantsQuery.error} />
      ) : (
      <Card>
        <CardHeader>
          <CardTitle>
            {t('tenants.title')} ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardBody>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-fg-subtle">
                  <th className="text-start font-medium pb-2">{t('tenants.table.company')}</th>
                  <th className="text-start font-medium pb-2">{t('tenants.table.trn')}</th>
                  <th className="text-start font-medium pb-2">{t('tenants.table.erp')}</th>
                  <th className="text-end font-medium pb-2">{t('tenants.table.invoices30d')}</th>
                  <th className="text-start font-medium pb-2">{t('tenants.table.status')}</th>
                  <th className="pb-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map((tenant) => (
                  <tr key={tenant.tenantId} className="group hover:bg-surface-muted/50 transition-colors">
                    <td className="py-3">
                      <Link to={`/tenants/${tenant.tenantId}`} className="font-medium text-fg hover:text-brand-700">
                        {tenant.companyNameEn}
                      </Link>
                      <div dir="rtl" lang="ar" className="text-xs text-fg-subtle">{tenant.companyNameAr}</div>
                    </td>
                    <td className="py-3 num" dir="ltr">{tenant.trn}</td>
                    <td className="py-3">{tenant.industry}</td>
                    <td className="py-3 text-end num">{numberFmt.format(tenant.invoicesYtd)}</td>
                    <td className="py-3">
                      <StatusBadge status={tenant.onboardingStatus} t={t} />
                    </td>
                    <td className="py-3">
                      <Link
                        to={`/tenants/${tenant.tenantId}`}
                        className="text-fg-subtle group-hover:text-brand-700 transition-colors"
                        aria-label={t('tenants.table.open')}
                      >
                        <ChevronRight className="h-4 w-4 rtl-mirror" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
      )}
    </div>
  );
}

function StatusBadge({
  status,
  t,
}: {
  status: AdminTenantRow['onboardingStatus'];
  t: (k: string) => string;
}) {
  const tone =
    status === 'ACTIVE' ? 'success' : status === 'PENDING' || status === 'SUSPENDED' ? 'warning' : 'danger';
  return <Badge tone={tone}>{t(`tenants.status.${status}`)}</Badge>;
}
